import { useContext, useState } from 'react';
import { nanoid } from 'nanoid';
import {
  deepClone,
  AccessibilityContext,
} from '@monash/portal-frontend-common';
import { Button, Icon, useResponsiveValue } from '@monash/portal-react';
import { MOBILE_RESPONSIVE } from '@/components/ui/main/Main';
import { WidgetContext } from '@/components/providers/WidgetProvider';

import DraggableWindow from '@/components/ui/draggable-window/DraggableWindow';
import SortableDragAndDropWrapper, {
  DND_TYPE,
} from '@/components/ui/drag-and-drop/SortableDragAndDropWrapper';
import { arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { EditCarparkDesktopRow } from './edit-carpark-row/EditCarparkDesktopRow';
import c from './edit-carparks.module.scss';
import { createPortal } from 'react-dom';
import { DragOverlay } from '@dnd-kit/core';
import { EditCarparkMobileRow } from './edit-carpark-row/EditCarparkMobileRow';
import { EditCarparksBottomSheet } from './edit-carparks-bottom-sheet/EditCarparksBottomSheet';

export const DROPDOWN_ITEM_CLASSNAME = 'carpark-dropdown-item';

export const EditCarparks = ({
  escapeFocusRef,
  initialCarparks,
  assembledParkingLocations,
  updateData,
}) => {
  const { editActiveWidget, setEditActiveWidget } = useContext(WidgetContext);
  const { setAppLivePoliteMsg, resetAppLiveMsgs } =
    useContext(AccessibilityContext);
  const responsiveSize = useResponsiveValue(MOBILE_RESPONSIVE);

  const [selectedCarparks, setSelectedCarparks] = useState(
    initialCarparks.length
      ? initialCarparks
      : [
          {
            id: nanoid(),
            value: '',
          },
        ]
  );

  // drag and drop
  const [activeId, setActiveId] = useState(null);
  const [bottomSheetCarparkId, setBottomSheetCarparkId] = useState(null);

  const activeCarpark = selectedCarparks.find(
    (carpark) => carpark.id === activeId
  );
  // onDragStart
  const activateItem = (e) => {
    setActiveId(e.active.id);
  };

  // onDragCancel
  const deactivateActiveItem = () => {
    setActiveId(null);
  };

  const isMobile = responsiveSize === 'S';
  const EditCarparkRow = isMobile
    ? EditCarparkMobileRow
    : EditCarparkDesktopRow;
  const HEADING_TEXT = 'Add/edit carparks';

  const getTriggerRefPosition = () => {
    if (!escapeFocusRef?.current) return;
    const position = escapeFocusRef.current.getBoundingClientRect();
    return { x: position.right, y: position.bottom };
  };

  const swapItemOrder = (e) => {
    const { active, over } = e;
    if (over && active.id !== over.id) {
      setSelectedCarparks((carparks) => {
        const newCarparks = deepClone(carparks);
        return arrayMove(
          newCarparks,
          newCarparks.findIndex((carpark) => carpark.id === active.id),
          newCarparks.findIndex((carpark) => carpark.id === over.id)
        );
      });
    }
    deactivateActiveItem();
  };

  const addNewCarpark = () => {
    const newCarpark = {
      value: '',
      id: nanoid(),
    };
    setSelectedCarparks((carparks) => [...carparks, newCarpark]);

    setTimeout(() => {
      const dropdowns = document.querySelectorAll(
        `.${DROPDOWN_ITEM_CLASSNAME} input`
      );
      dropdowns[dropdowns.length - 1].focus();
    }, 100);
  };

  const deleteCarpark = (id) => {
    const carpark = selectedCarparks.find((c) => c.id === id);
    const enrichedCarpark = assembledParkingLocations.find(
      (location) => location.id === carpark?.value
    );
    const carparkName = enrichedCarpark?.name || 'unselected carpark';

    resetAppLiveMsgs();

    setTimeout(() => {
      setAppLivePoliteMsg(`${carparkName}, deleted`);
    }, 200);

    setSelectedCarparks((selectedCarpark) =>
      selectedCarpark.filter((carpark) => carpark.id !== id)
    );
    setBottomSheetCarparkId(null);
  };

  const moveUp = (index) => {
    if (index > 0) {
      setSelectedCarparks((items) => {
        const itemsClone = [...items];
        return arrayMove(itemsClone, index, index - 1);
      });
    }
  };

  const moveDown = (index) => {
    if (index < selectedCarparks.length - 1) {
      setSelectedCarparks((items) => {
        const itemsClone = [...items];
        return arrayMove(itemsClone, index, index + 1);
      });
    }
  };

  const save = () => {
    updateData(
      'selectedCarparks',
      selectedCarparks.filter((carpark) => Boolean(carpark.value))
    );

    resetAppLiveMsgs();

    setTimeout(() => {
      setAppLivePoliteMsg('Clayton Parking widget, saved');
    }, 200);

    onClose();
  };

  const onClose = () => {
    escapeFocusRef.current?.focus();
    setEditActiveWidget(null);
  };

  const bottomSheetCarparkIndex = selectedCarparks.findIndex(
    (carpark) => bottomSheetCarparkId === carpark.id
  );

  return (
    <>
      <DraggableWindow
        fullScreen={isMobile}
        title={HEADING_TEXT}
        isShowing={editActiveWidget}
        focusTrapEnabled
        focusTrapSelectors="button:not([disabled]), input[type=text], input[type=url]"
        onClose={onClose}
        initialPosition={getTriggerRefPosition()}
        extraRootProps={{
          role: 'dialog',
          'aria-label': HEADING_TEXT,
        }}
        keyboardDismissalEnabled
      >
        <div className={c.wrapper}>
          <div className={c.content}>
            <div className={c.gridHeader}>
              <h4 className={c.name}>Name</h4>
            </div>

            <SortableDragAndDropWrapper
              sortableItems={selectedCarparks}
              onDragEnd={swapItemOrder}
              strategy={verticalListSortingStrategy}
              dndType={DND_TYPE.VERTICAL}
              onDragStart={activateItem}
            >
              <ul className={c.carparkList}>
                {selectedCarparks.map((selectedCarpark, index) => (
                  <EditCarparkRow
                    key={selectedCarpark.id}
                    selectedCarpark={selectedCarpark}
                    selectedCarparks={selectedCarparks}
                    setSelectedCarparks={setSelectedCarparks}
                    assembledParkingLocations={assembledParkingLocations}
                    index={index}
                    onOpenBottomSheet={
                      isMobile
                        ? () => setBottomSheetCarparkId(selectedCarpark.id)
                        : undefined
                    }
                    isEditing={selectedCarpark.id === bottomSheetCarparkId}
                  />
                ))}
              </ul>
              {createPortal(
                <DragOverlay className={c.DragOverlayWrapper}>
                  <EditCarparkRow
                    selectedCarpark={activeCarpark}
                    selectedCarparks={selectedCarparks}
                    setSelectedCarparks={setSelectedCarparks}
                    assembledParkingLocations={assembledParkingLocations}
                  />
                </DragOverlay>,
                document.getElementById('root') || document.body
              )}
            </SortableDragAndDropWrapper>
          </div>

          <div className={c.actions}>
            <Button
              variant="text"
              size="small"
              icon={<Icon.Plus />}
              onClick={addNewCarpark}
              onKeyDown={(e) => e.stopPropagation()}
              mode="card"
            >
              Add carpark
            </Button>
            <div>
              <Button
                variant="text"
                size="small"
                onClick={onClose}
                onKeyDown={(e) => e.stopPropagation()}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="small"
                onClick={save}
                onKeyDown={(e) => e.stopPropagation()}
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      </DraggableWindow>

      <EditCarparksBottomSheet
        isOpen={Boolean(bottomSheetCarparkId)}
        onClose={() => setBottomSheetCarparkId(null)}
        selectedCarparks={selectedCarparks}
        assembledParkingLocations={assembledParkingLocations}
        onMoveUp={() => moveUp(bottomSheetCarparkIndex)}
        onMoveDown={() => moveDown(bottomSheetCarparkIndex)}
        index={bottomSheetCarparkIndex}
        onDelete={() => deleteCarpark(bottomSheetCarparkId)}
      />
    </>
  );
};
